import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type { Carate, Carecommendation, Period } from 'lumen/models';
import { USER_ENGAGEMENT_ELEMENT_IDS } from 'lumen/models/seen-user-engagement-element';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RawRecommendationData } from 'lumen/services/pricing-assistant-data-request-service';
import type { PricingOverridePayload } from 'lumen/services/pricing-assistant-handle-recommendation-actions-service';
import {
  NEW_PRICE_LOWER_LIMIT_FACTOR,
  NEW_PRICE_UPPER_LIMIT_FACTOR,
  OVERRIDE_TYPES,
  OVERRIDE_TYPES_USAGE_TRACKING,
  type OverrideVariant,
  type PricePreviewRooms,
  type PricingOverrideTypes,
  RECOMMENDATION_ACTION_STATES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { getOverrideVariant } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import { isCarateForRateCode } from 'lumen/utils/pricing-assistant/open-pricing-utils';
import { getPushErrorTranslation } from 'lumen/utils/pricing-assistant/push-error-message-translation';
import { timeout } from 'lumen/utils/waiters';

import DiscountOverrideForm from './discount-override-form';
import PriceOverrideForm from './price-override-form';
import PricePreview from './price-preview';
import ReplaceRangeWarningModal from './replace-range-warning-modal';

interface HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModalSignature {
  Args: {
    carate: Carate;
    onCloseModal: () => void;
    pricePreviewRooms: PricePreviewRooms;
    rateCode: string | null;
  };
  Blocks: {
    default: [];
  };
}

type RecommendationPair = {
  carate: Carate;
  carecommendation: Carecommendation | null;
};

interface OverrideError {
  errorMessage: string;
  hasErrors: boolean;
  showErrors: boolean;
}

interface OverrideWarning {
  hasWarnings: boolean;
  warningMessage: string;
}

interface PricePreviewSimulatedPrice {
  formattedCurrentPrice: string | null;
  formattedOverriddenPrice: string | null;
  overrideVariant?: OverrideVariant;
  roomName: string;
  showOverride: boolean;
}

export default class HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModal extends Component<HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModalSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service('user-engagement')
  declare userEngagementService: Services['user-engagement'];

  @tracked attemptedToSubmit: 'discount' | 'exact' | 'range' | null = null;
  @tracked discountOverrideValue: number | null = null;
  @tracked exactPriceOverrideNewPrice: number | null = null;
  @tracked hasPendingSend = false;
  @tracked priceRangeOverrideMaxPrice: number | null = null;
  @tracked priceRangeOverrideMinPrice: number | null = null;
  @tracked selectedPriceOverrideType: PricingOverrideTypes =
    OVERRIDE_TYPES.PRICE_RANGE;
  /*
    Keep track of the simulated recommendations for the override being edited (these are not of type carecommendation model instead raw data from the API)
  */
  @tracked simulatedRecommendationsResponse?: RawRecommendationData[] | null =
    null;

  debouncedUpdateValueTask = task(
    { restartable: true },
    async (
      type: 'exact' | 'min_range' | 'max_range' | 'discount',
      value: number | null,
    ): Promise<void> => {
      await timeout();

      switch (type) {
        case 'exact':
          this.exactPriceOverrideNewPrice = value;
          break;
        case 'min_range':
          this.priceRangeOverrideMinPrice = value;
          break;
        case 'max_range':
          this.priceRangeOverrideMaxPrice = value;
          break;
        case 'discount':
          this.discountOverrideValue = value;
          break;
      }
    },
  );

  get discountOverrideNewPriceError(): OverrideError {
    if (this.discountOverrideValue === null) {
      return {
        hasErrors: true,
        showErrors: this.attemptedToSubmit === 'discount',
        errorMessage: this.intl.t(
          'pricing_assistant.override_edit.discount.empty',
        ),
      };
    }

    if (this.discountOverrideValue >= 100) {
      return {
        hasErrors: true,
        showErrors: this.attemptedToSubmit === 'discount',
        errorMessage: this.intl.t(
          'pricing_assistant.override_edit.discount.higher',
        ),
      };
    }

    return {
      hasErrors: false,
      showErrors: false,
      errorMessage: '',
    };
  }

  get discountPlaceholder(): string | undefined {
    const { currentDiscount } = this.args.carate;
    if (currentDiscount == null) {
      return undefined;
    }

    return String(Math.round(currentDiscount * 100));
  }

  get exactPriceOverrideNewPriceError(): OverrideError {
    if (this.exactPriceOverrideNewPrice === null) {
      return {
        hasErrors: true,
        showErrors: this.attemptedToSubmit === 'exact',
        errorMessage: this.intl.t(
          'pricing_assistant.override_edit_type.current_price.error',
        ),
      };
    }

    return {
      hasErrors: false,
      showErrors: false,
      errorMessage: '',
    };
  }

  get exactPriceOverrideNewPriceWarning(): OverrideWarning {
    const newPrice = this.exactPriceOverrideNewPrice;
    if (this.exactPriceOverrideNewPriceError.hasErrors || newPrice === null) {
      return { warningMessage: '', hasWarnings: false };
    }

    if (
      this.args.carate.userSetMinimum &&
      newPrice < this.args.carate.userSetMinimum
    ) {
      return {
        warningMessage: this.intl.t(
          'pricing_assistant.edit_price.warning.lower_than_settings',
          {
            minSettingsPrice: this.hotelAndMarketService.formatRate(
              this.args.carate.userSetMinimum,
            ),
            htmlSafe: true,
          },
        ),
        hasWarnings: true,
      };
    }

    if (
      this.args.carate.userSetMaximum &&
      newPrice > this.args.carate.userSetMaximum
    ) {
      return {
        warningMessage: this.intl.t(
          'pricing_assistant.edit_price.warning.higher_than_settings',
          {
            maxSettingsPrice: this.hotelAndMarketService.formatRate(
              this.args.carate.userSetMaximum,
            ),
            htmlSafe: true,
          },
        ),
        hasWarnings: true,
      };
    }

    const priceToCheck = this.args.carate.value;
    const isTooLow = newPrice < priceToCheck * NEW_PRICE_LOWER_LIMIT_FACTOR;
    const isTooHigh = newPrice > priceToCheck * NEW_PRICE_UPPER_LIMIT_FACTOR;

    if (isTooLow) {
      return {
        warningMessage: this.intl.t('pricing_assistant.edit_price.warning.low'),
        hasWarnings: true,
      };
    }

    if (isTooHigh) {
      return {
        warningMessage: this.intl.t(
          'pricing_assistant.edit_price.warning.high',
        ),
        hasWarnings: true,
      };
    }

    return { warningMessage: '', hasWarnings: false };
  }

  get exactPricePlaceholder(): string | undefined {
    return this.formatPlaceholder(
      this.args.carate.dynamicRatePricingPriceToShow,
    );
  }

  get isPriceOverrideType(): boolean {
    return (
      this.overrideType === OVERRIDE_TYPES.EXACT_PRICE ||
      this.overrideType === OVERRIDE_TYPES.PRICE_RANGE
    );
  }

  get isSendButtonDisabled(): boolean {
    return this.pricingAssistantDataRequestService
      .launchOverrideSimulateRequestTask.isRunning;
  }

  get modalTitle(): string {
    const { roomName } = this.args.carate;
    const day = this.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );

    /**
     * As per the Specs, the modal header for the discount override shows 'All Rooms' instead of the Room Name that I clicked on to open the modal.
     * This is because regardless of which room I click on, the discount override is applied to all rooms.
     */
    const appliesTo =
      this.overrideType === OVERRIDE_TYPES.DISCOUNT
        ? this.intl.t('pricing_assistant.all_rooms')
        : roomName;

    return `${this.intl.t('general.edit')} ${day}, ${appliesTo}`;
  }

  get overridePayload(): PricingOverridePayload {
    const { pmsRoomtype, rateCode } = this.args.carate;
    return {
      rate_code: rateCode,
      pms_roomtype: pmsRoomtype,
      from_date: this.period.fromDate.format('YYYY-MM-DD'),
      subscription_id: parseInt(this.hotelAndMarketService.hotel.id!, 10),
      value: this.overrideValue,
      type: this.overrideType,
    };
  }

  get overrideType(): PricingOverrideTypes {
    const { carate } = this.args;
    const { primaDefaultStaticRateCode } = this.hotelAndMarketService.hotel;
    if (isCarateForRateCode(carate, primaDefaultStaticRateCode)) {
      return this.selectedPriceOverrideType;
    }

    return OVERRIDE_TYPES.DISCOUNT;
  }

  get overrideValue(): PricingOverridePayload['value'] {
    if (this.overrideType === OVERRIDE_TYPES.EXACT_PRICE) {
      return this.exactPriceOverrideNewPrice;
    }

    if (this.overrideType === OVERRIDE_TYPES.PRICE_RANGE) {
      return {
        minimum: this.priceRangeOverrideMinPrice,
        maximum: this.priceRangeOverrideMaxPrice,
      };
    }

    return this.discountOverrideValue !== null
      ? this.discountOverrideValue / 100
      : null;
  }

  get period(): Period {
    return this.args.carate.period;
  }

  get pricePreviewSimulatedPrices(): PricePreviewSimulatedPrice[] {
    return this.args.pricePreviewRooms.map((room) => {
      const simulatedRecommendation =
        this.simulatedRecommendationsForSelectedRateCode.find(
          (simulatedPrice) => {
            return simulatedPrice.pms_roomtype === room.pmsRoomType;
          },
        );

      const simulatedValue = simulatedRecommendation?.recommended_value ?? null;

      return {
        roomName: room.roomName,
        formattedCurrentPrice: room.formattedCurrentPrice ?? null,
        formattedOverriddenPrice:
          simulatedValue !== room.currentPrice
            ? this.hotelAndMarketService.formatRate(simulatedValue, {
                longFormat: false,
              })
            : null,
        overrideVariant: getOverrideVariant(room.overrideType),
        showOverride: room.showOverride,
      };
    });
  }

  get priceRangeMaxPlaceholder(): string | undefined {
    return this.formatPlaceholder(this.args.carate.userSetMaximum);
  }

  get priceRangeMinPlaceholder(): string | undefined {
    return this.formatPlaceholder(this.args.carate.userSetMinimum);
  }

  get priceRangeOverrideErrors(): {
    priceRangeOverrideMaxPriceError: OverrideError;
    priceRangeOverrideMinPriceError: OverrideError;
  } {
    const noErrorState = {
      hasErrors: false,
      showErrors: false,
      errorMessage: '',
    };

    const rangeOverrideErrors = {
      priceRangeOverrideMaxPriceError: noErrorState,
      priceRangeOverrideMinPriceError: noErrorState,
    };

    const areBothPriceRangeFieldsEmpty =
      this.priceRangeOverrideMinPrice === null &&
      this.priceRangeOverrideMaxPrice === null;

    if (areBothPriceRangeFieldsEmpty) {
      rangeOverrideErrors.priceRangeOverrideMinPriceError = {
        hasErrors: true,
        showErrors: this.attemptedToSubmit === 'range',
        errorMessage: this.intl.t(
          'pricing_assistant.override_edit_type.min_price.error',
        ),
      };
      rangeOverrideErrors.priceRangeOverrideMaxPriceError = {
        hasErrors: true,
        showErrors: this.attemptedToSubmit === 'range',
        errorMessage: '',
      };

      return rangeOverrideErrors;
    }

    const isMinPriceGreaterThanMaxPrice =
      this.priceRangeOverrideMinPrice !== null &&
      this.priceRangeOverrideMaxPrice !== null &&
      this.priceRangeOverrideMinPrice > this.priceRangeOverrideMaxPrice;

    if (isMinPriceGreaterThanMaxPrice) {
      rangeOverrideErrors.priceRangeOverrideMinPriceError = {
        hasErrors: true,
        showErrors: this.attemptedToSubmit === 'range',
        errorMessage: this.intl.t(
          'pricing_assistant.override_edit_type.min_price.error_greater_than_max',
        ),
      };
      rangeOverrideErrors.priceRangeOverrideMaxPriceError = {
        hasErrors: true,
        showErrors: this.attemptedToSubmit === 'range',
        errorMessage: '',
      };
    }

    return rangeOverrideErrors;
  }

  get pushErrorMessage(): string {
    return getPushErrorTranslation(
      this.args.carate.carecommendation?.pushResultMessage,
      this.intl,
    );
  }

  get shouldShowPriceRangeReplaceWarning(): boolean {
    const { override } = this.args.carate;
    const hasExistingPriceRangeOverride =
      override?.type === OVERRIDE_TYPES.PRICE_RANGE;

    return (
      this.overrideType === OVERRIDE_TYPES.PRICE_RANGE &&
      hasExistingPriceRangeOverride &&
      !this.userEngagementService.hasSeen(
        USER_ENGAGEMENT_ELEMENT_IDS.PRICE_RANGE_OVERRIDE_REPLACE_WARNING,
      )
    );
  }

  get showException(): boolean {
    return Boolean(this.args.carate.carecommendation?.hasException);
  }

  get showOverrides(): boolean {
    return this.pricePreviewSimulatedPrices.some(
      (simulatedPrice) => simulatedPrice.showOverride,
    );
  }

  get simulatedRecommendationsForSelectedRateCode(): RawRecommendationData[] {
    return (this.simulatedRecommendationsResponse ?? []).filter(
      ({ rate_code }) => rate_code === this.args.rateCode,
    );
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModalSignature['Args'],
  ) {
    super(owner, args);

    const { carate } = this.args;

    if (!carate || !carate.override) {
      return;
    }

    if (carate.override.type === OVERRIDE_TYPES.EXACT_PRICE) {
      this.exactPriceOverrideNewPrice = carate.override.value;
      this.selectedPriceOverrideType = OVERRIDE_TYPES.EXACT_PRICE;
    }

    if (carate.override.type === OVERRIDE_TYPES.PRICE_RANGE) {
      const { minimum, maximum } = carate.override.value;
      this.priceRangeOverrideMinPrice = minimum;
      this.priceRangeOverrideMaxPrice = maximum;
      this.selectedPriceOverrideType = OVERRIDE_TYPES.PRICE_RANGE;
    }
    if (carate.override.type === OVERRIDE_TYPES.DISCOUNT) {
      this.discountOverrideValue = carate.override.value * 100;
      this.selectedPriceOverrideType = OVERRIDE_TYPES.DISCOUNT;
    }
  }

  private executeSend(): void {
    this.trackOverrideEnabled();
    // It can happen that override causes current recommendations to be removed for any room type even if they were not part of the simulation response
    // In this case we need to make sure those recommendations are also marked as affected by the override push so that they get refreshed properly
    // Therefore, we inform the poller about all room type recommendations for the override period
    const allCarecommendationsForPeriod = this.period.carates.flatMap(
      (carate) => carate.carecommendation ?? [],
    );

    // Keyed by the override day so its loading state turns on even when the day has no recommendations
    this.pricingAssistantRecommendationActionPoller.addRecommendationsAffectedByOverridePush(
      { [this.overridePayload.from_date]: allCarecommendationsForPeriod },
    );

    const { carecommendationAndCaratePairs, newPrices } =
      this.processSimulatedRecommendationsForActionTask(
        this.simulatedRecommendationsResponse,
      );

    void this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationActionBulkPush(
      carecommendationAndCaratePairs,
      RECOMMENDATION_ACTION_STATES.ACCEPTED,
      newPrices,
      null,
      this.overridePayload,
    );

    this.onCloseModal();
  }

  async executeSimulateRequest(): Promise<
    RawRecommendationData[] | null | undefined
  > {
    /* Cancel any in-flight simulate — a stale response must not overwrite state when validation blocks a new request. */
    await this.pricingAssistantDataRequestService.launchOverrideSimulateRequestTask.cancelAll();

    if (
      this.overrideType === OVERRIDE_TYPES.EXACT_PRICE &&
      this.exactPriceOverrideNewPriceError.hasErrors
    ) {
      return;
    }

    if (
      this.overrideType === OVERRIDE_TYPES.DISCOUNT &&
      this.discountOverrideNewPriceError.hasErrors
    ) {
      return;
    }

    if (
      this.overrideType === OVERRIDE_TYPES.PRICE_RANGE &&
      (this.priceRangeOverrideErrors.priceRangeOverrideMinPriceError
        .hasErrors ||
        this.priceRangeOverrideErrors.priceRangeOverrideMaxPriceError.hasErrors)
    ) {
      return;
    }

    try {
      return await this.pricingAssistantDataRequestService.launchOverrideSimulateRequestTask.perform(
        this.hotelAndMarketService.hotel.id!,
        this.overridePayload,
      );
    } catch (error: unknown) {
      if (error && (error as { name: string }).name === 'TaskCancelation') {
        return; // Expected cancellation
      }
      throw error;
    }
  }

  private formatPlaceholder(value: number | null): string | undefined {
    return (
      this.hotelAndMarketService.formatRate(value, {
        includeCurrencySymbol: false,
        longFormat: false,
      }) ?? undefined
    );
  }

  /*
    Process the simulated recommendations response to extract carecommendation and carate pairs along with new prices
  */
  processSimulatedRecommendationsForActionTask(
    simulatedRecommendations: RawRecommendationData[] | null | undefined,
  ): {
    carecommendationAndCaratePairs: RecommendationPair[];
    newPrices: Record<string, number>;
  } {
    const result: {
      carecommendationAndCaratePairs: RecommendationPair[];
      newPrices: Record<string, number>;
    } = {
      carecommendationAndCaratePairs: [],
      newPrices: {},
    };

    if (!simulatedRecommendations?.length) {
      return result;
    }

    // Filter relevant recommendations for the current carate's rate code
    // Note: BE reuses their existing recommendation flow and return all the recommendations for the day override is applied to
    const relevantRecommendations = simulatedRecommendations
      .map((rawRecommendation) => {
        const carate = this.store.peekRecord<Carate>(
          'carate',
          rawRecommendation.carate,
        );

        // Static prices overrides (exact price and price range) affect all price recommendations for the day,
        // Dynamic discount overrides affect only price recommendations matching their rate code
        // Therefore for overrides of type DISCOUNT it takes into account only the recommendations that match the rate code of the override being edited.
        // For all other override types, it sends all recommendations returned for the day.
        if (
          !carate ||
          !this.args.rateCode ||
          (this.overrideType === OVERRIDE_TYPES.DISCOUNT &&
            !isCarateForRateCode(carate, this.args.rateCode))
        ) {
          return null;
        }
        return { rawRecommendation, carate };
      })
      // Remove the null entries
      .filter(
        (
          item,
        ): item is {
          carate: Carate;
          rawRecommendation: RawRecommendationData;
        } => item !== null,
      );

    // Process each relevant recommendation
    relevantRecommendations.forEach(({ rawRecommendation, carate }) => {
      const carecommendation = this.store.peekRecord<Carecommendation>(
        'carecommendation',
        rawRecommendation.id,
      );

      result.carecommendationAndCaratePairs.push({
        carecommendation,
        carate,
      });

      if (carate.id) {
        result.newPrices[carate.id] = rawRecommendation.recommended_value;
      }
    });

    return result;
  }

  private trackOverrideEnabled(): void {
    this.usageAnalytics.track('Autopilot Override Enabled', {
      eventTrigger: 'table-tile',
      overrideType: OVERRIDE_TYPES_USAGE_TRACKING[this.overrideType],
    });
  }

  @action
  onCloseModal(): void {
    this.args.onCloseModal();
  }

  @action
  async onOverrideEditTypeChange(event: Event): Promise<void> {
    await this.pricingAssistantDataRequestService.launchOverrideSimulateRequestTask.cancelAll();
    this.simulatedRecommendationsResponse = null;

    this.selectedPriceOverrideType = (event.target as HTMLInputElement)
      .value as PricingOverrideTypes;

    this.attemptedToSubmit = null;
    this.simulatedRecommendationsResponse = await this.executeSimulateRequest();
  }

  @action
  onReplaceWarningCancel(): void {
    this.hasPendingSend = false;
  }

  @action
  onReplaceWarningConfirm(): void {
    this.hasPendingSend = false;
    this.executeSend();
  }

  @action
  onSendOverrideEdit(): void {
    switch (this.overrideType) {
      case OVERRIDE_TYPES.EXACT_PRICE:
        this.attemptedToSubmit = 'exact';
        break;
      case OVERRIDE_TYPES.PRICE_RANGE:
        this.attemptedToSubmit = 'range';
        break;
      case OVERRIDE_TYPES.DISCOUNT:
        this.attemptedToSubmit = 'discount';
        break;
    }

    if (
      this.overrideType === OVERRIDE_TYPES.EXACT_PRICE &&
      this.exactPriceOverrideNewPriceError.hasErrors
    ) {
      return;
    }

    if (
      this.overrideType === OVERRIDE_TYPES.PRICE_RANGE &&
      this.priceRangeOverrideErrors.priceRangeOverrideMinPriceError.hasErrors
    ) {
      return;
    }

    if (
      this.overrideType === OVERRIDE_TYPES.DISCOUNT &&
      this.discountOverrideNewPriceError.hasErrors
    ) {
      return;
    }

    if (this.shouldShowPriceRangeReplaceWarning) {
      this.hasPendingSend = true;
      return;
    }

    this.executeSend();
  }

  @action
  onValueChange(
    type: 'exact' | 'min_range' | 'max_range' | 'discount',
  ): (value: number | null) => Promise<void> {
    return async (value: number | null): Promise<void> => {
      try {
        await this.debouncedUpdateValueTask.perform(type, value);
      } catch (error: unknown) {
        if (error && (error as { name: string }).name === 'TaskCancelation') {
          return; // Expected cancellation
        }

        throw error;
      }

      this.simulatedRecommendationsResponse =
        await this.executeSimulateRequest();
    };
  }

  <template>
    {{#if this.hasPendingSend}}
      <ReplaceRangeWarningModal
        @onCancel={{this.onReplaceWarningCancel}}
        @onConfirm={{this.onReplaceWarningConfirm}}
      />
    {{else}}
      <PrismModal
        @onClose={{this.onCloseModal}}
        @title={{this.modalTitle}}
        data-test-id="edit-override-modal"
      >
        <:default>
          {{#if this.showException}}
            <PrismBanner
              @bannerMessage={{this.pushErrorMessage}}
              @bannerStyle="critical"
              @bannerTitle={{t
                "pricing_assistant.exception.action_state.title"
              }}
              @icon="circle-minus"
              @position="top"
            />
          {{/if}}
          <div class="flex flex-col gap-400 p-500">
            {{#if this.isPriceOverrideType}}
              <PriceOverrideForm
                @exactPriceOverrideNewPrice={{this.exactPriceOverrideNewPrice}}
                @exactPriceOverrideNewPriceError={{this.exactPriceOverrideNewPriceError}}
                @exactPriceOverrideNewPriceWarning={{this.exactPriceOverrideNewPriceWarning}}
                @exactPricePlaceholder={{this.exactPricePlaceholder}}
                @onOverrideEditTypeChange={{this.onOverrideEditTypeChange}}
                @onValueChange={{this.onValueChange}}
                @priceRangeMaxPlaceholder={{this.priceRangeMaxPlaceholder}}
                @priceRangeMinPlaceholder={{this.priceRangeMinPlaceholder}}
                @priceRangeOverrideErrors={{this.priceRangeOverrideErrors}}
                @priceRangeOverrideMaxPrice={{this.priceRangeOverrideMaxPrice}}
                @priceRangeOverrideMinPrice={{this.priceRangeOverrideMinPrice}}
                @selectedOverrideEditType={{this.selectedPriceOverrideType}}
              />
            {{/if}}

            {{#if (eq this.overrideType OVERRIDE_TYPES.DISCOUNT)}}
              <DiscountOverrideForm
                @discountOverrideNewValueError={{this.discountOverrideNewPriceError}}
                @discountPlaceholder={{this.discountPlaceholder}}
                @onValueChange={{fn (this.onValueChange "discount")}}
                @value={{this.discountOverrideValue}}
              />
            {{/if}}

            <PricePreview
              @isLoadingSimulatedPrices={{this.pricingAssistantDataRequestService.launchOverrideSimulateRequestTask.isRunning}}
              @overrideType={{this.overrideType}}
              @roomName={{@carate.roomName}}
              @showOverrides={{this.showOverrides}}
              @simulatedPrices={{this.pricePreviewSimulatedPrices}}
            />
          </div>
        </:default>
        <:footer>
          <div
            class="mx-500 flex h-full flex-row items-center justify-end gap-200"
          >
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.cancel"}}
              @onClick={{this.onCloseModal}}
            />
            <PrismButton
              @buttonType="primary"
              @iconBefore="send"
              @isDisabled={{this.isSendButtonDisabled}}
              @label={{t "pricing_assistant.label.push"}}
              @onClick={{this.onSendOverrideEdit}}
              data-test-id="override-edit-send-button"
            />
          </div>
        </:footer>
      </PrismModal>
    {{/if}}
  </template>
}
